import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ApiError } from "../../lib/api";
import { authApi } from "../../lib/auth";

const VerifyEmailPendingPage = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const postJobIntent = searchParams.get("intent") === "post-job";
  const [isResending, setIsResending] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [feedbackTone, setFeedbackTone] = useState<"info" | "error">("info");

  const handleResend = async () => {
    if (!email || isResending) {
      return;
    }

    setIsResending(true);
    setFeedback(null);
    try {
      await authApi.csrf();
      await authApi.resendEmailVerification({ email, postJobIntent });
      setFeedbackTone("info");
      setFeedback("Verification email sent. Check your inbox and spam folder.");
    } catch (error) {
      setFeedbackTone("error");
      if (error instanceof ApiError && error.status === 429) {
        setFeedback("You have reached the resend limit for now. Please wait before trying again.");
      } else {
        setFeedback("Unable to resend verification email right now.");
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <main className="min-h-screen bg-myteengig-background px-6 py-16">
      <div className="mx-auto max-w-2xl overflow-hidden rounded-[32px] border border-myteengig-border bg-white shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
        <div className="bg-[linear-gradient(135deg,rgba(37,99,235,0.08),rgba(16,185,129,0.08))] px-8 py-10">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-myteengig-primary">Account Created</p>
          <h1 className="mt-3 text-2xl font-bold text-myteengig-text-primary sm:text-3xl">Check your email to verify your account</h1>
          <p className="mt-4 max-w-xl text-base text-myteengig-text-muted">
            We sent a verification link
            {email ? ` to ${email}` : " to the email address you used to sign up"}.
            Open that email and click the link before logging in.
          </p>
          <p className="mt-3 max-w-xl text-sm font-semibold text-myteengig-text-primary">
            Check your spam or promotions folder if you do not see it in your inbox.
          </p>
        </div>

        <div className="px-8 py-8">
          <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-5">
            <p className="text-sm font-medium text-myteengig-text-primary">What happens next</p>
            <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">
              Once your email is verified, return here and log in normally.
            </p>
          </div>

          {email ? (
            <div className="mt-5 rounded-2xl border border-myteengig-border bg-white p-5">
              <p className="text-sm font-medium text-myteengig-text-primary">Need another link?</p>
              <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">
                You can request another verification email if the first one expired or never arrived.
              </p>
              <div className="mt-4 flex flex-col items-center gap-3 text-center sm:flex-row sm:text-left">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isResending}
                  className="inline-flex items-center justify-center rounded-full border border-myteengig-primary px-5 py-3 text-sm font-semibold text-myteengig-primary transition hover:bg-myteengig-primary/5 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isResending ? "Sending..." : "Resend verification email"}
                </button>
                <p className="text-xs text-myteengig-text-muted">Limited to 3 per hour and 5 per day.</p>
              </div>
              {feedback ? (
                <p className={`mt-3 text-sm ${feedbackTone === "error" ? "text-red-600" : "text-myteengig-text-primary"}`}>
                  {feedback}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="mt-8">
            <Link
              to="/login"
              className="inline-flex items-center rounded-full bg-myteengig-primary px-5 py-3 text-sm font-semibold text-white transition hover:bg-myteengig-primary-hover"
            >
              Go to login
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
};

export default VerifyEmailPendingPage;
